import React, { useEffect, useState } from "react";
import {
  FolderOpen,
  Plus,
  Users,
  TrendingUp,
  ShoppingCart,
  Package,
  Calendar,
  DollarSign,
  Clock,
  CheckCircle,
  Truck,
} from "lucide-react";
import { getStatsSummary, isShop } from "../lib/pocketbase";
import { StatsSummary } from "../types";
import ShopDashboardPage from "./ShopDashboardPage";

const DashboardPage: React.FC = () => {
  const shop = isShop();
  const [stats, setStats] = useState<StatsSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (shop) {
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        const statsData = await getStatsSummary();
        setStats(statsData);
      } catch (error: any) {
        // Ignore auto-cancellation errors
        if (error?.message?.includes("autocancelled")) {
          return;
        }
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [shop]);

  if (shop) {
    return <ShopDashboardPage />;
  }

  const dashboardStats = stats
    ? [
        {
          name: "کل کاربران",
          value: stats.total_users,
          icon: Users,
          color: "bg-blue-500",
        },
        {
          name: "کل سفارشات",
          value: stats.total_orders,
          icon: ShoppingCart,
          color: "bg-green-500",
        },
        {
          name: "سفارشات این ماه",
          value: stats.current_month_orders,
          icon: Calendar,
          color: "bg-purple-500",
        },
        {
          name: "کل محصولات",
          value: stats.total_products,
          icon: Package,
          color: "bg-orange-500",
        },
      ]
    : [];

  const monthlyOrderStats = stats
    ? [
        {
          name: "در حال آماده سازی",
          value: stats.current_month_pending_orders,
          icon: Clock,
          color: "bg-yellow-500",
        },
        {
          name: "آماده شده",
          value: stats.current_month_done_orders,
          icon: CheckCircle,
          color: "bg-green-500",
        },
        {
          name: "تحویل",
          value: stats.current_month_delivery_orders,
          icon: Truck,
          color: "bg-blue-500",
        },
      ]
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">داشبورد</h1>
        <p className="text-gray-600">نمای کلی سیستم مدیریت</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {dashboardStats.map((stat) => (
          <div key={stat.name} className="card p-6">
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
              <div className="mr-4">
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Monthly Order Status Breakdown */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          وضعیت سفارشات این ماه
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {monthlyOrderStats.map((stat) => (
            <div key={stat.name} className="card p-4">
              <div className="flex items-center">
                <div className={`p-2 rounded-lg ${stat.color}`}>
                  <stat.icon className="h-5 w-5 text-white" />
                </div>
                <div className="mr-3">
                  <p className="text-sm font-medium text-gray-600">
                    {stat.name}
                  </p>
                  <p className="text-xl font-bold text-gray-900">
                    {stat.value}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
